// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: konvo:// links to things Konvo opens itself, which the Assistant puts
// in replies instead of Outlook links: an event (the event window), a mail (the
// preview window) and a draft (the composer). The Rail cannot show these, so it
// hands them to the main window. Openers load lazily: they bring their stores.

import {sourceItemFromHref} from '@app/features/konvo_assistant/utils/AssistantSourceLinks';
import {isRailWindow} from '@app/features/konvo_rail/utils/RailWindow';

export type KonvoItemLink = {kind: 'event'; id: string} | {kind: 'mail'; id: string} | {kind: 'mail_draft'; id: string};

const ITEM_LINK = /^konvo:\/\/(?:open\/)?(calendar\/events|mail\/messages|mail\/drafts)\/([^/?#\s]+)/i;
const KINDS: Record<string, KonvoItemLink['kind']> = {
	'calendar/events': 'event',
	'mail/messages': 'mail',
	'mail/drafts': 'mail_draft',
};

export function parseKonvoItemLink(href: string): KonvoItemLink | null {
	const match = ITEM_LINK.exec(href.trim());
	if (!match) return null;
	let id: string;
	try {
		id = decodeURIComponent(match[2]!);
	} catch {
		return null;
	}
	const kind = KINDS[match[1]!.toLowerCase()];
	return kind && id ? {kind, id} : null;
}

export function konvoItemHref(item: KonvoItemLink): string {
	const path = item.kind === 'event' ? 'calendar/events' : item.kind === 'mail' ? 'mail/messages' : 'mail/drafts';
	return `konvo://${path}/${encodeURIComponent(item.id)}`;
}

/** Opens the item; false when the link is not one of these. */
export function openKonvoItemLink(href: string, label = ''): boolean {
	const item = parseKonvoItemLink(href);
	if (!item) return false;
	openKonvoItem(item, label);
	return true;
}

export function openKonvoItem(item: KonvoItemLink, label = ''): void {
	if (item.kind === 'mail') {
		// A mail is a source: the preview window, or the main window from the Rail.
		const source = sourceItemFromHref(konvoItemHref(item), label);
		if (source) {
			void import('@app/features/konvo_assistant/utils/AssistantSourceOpen').then((module) =>
				module.openAssistantSource(konvoItemHref(item), label),
			);
		}
		return;
	}
	if (isRailWindow()) {
		void Promise.all([import('@app/features/konvo_rail/RailHost'), import('@app/features/konvo_rail/RailRoutes')]).then(
			([host, routes]) =>
				host.openInMainWindow(item.kind === 'event' ? routes.railEventRoute(item.id) : routes.railDraftRoute(item.id)),
		);
		return;
	}
	if (item.kind === 'event') {
		void import('@app/features/konvo_calendar/commands/OpenCalendarEvent').then((module) =>
			module.openCalendarEventById(item.id),
		);
		return;
	}
	void import('@app/features/konvo_mail/commands/OpenMailDraft').then((module) => module.openMailDraft(item.id));
}
